import {
  DvCheckboxCellEditor,
  DvCheckboxCellRenderer,
  DvIconCellRendererParams,
  DvIconHeaderRendererParams,
  DvNumberCellEditor,
  DvTextCellEditor,
  DvToolbarAction,
  DvToolbarActionEvent,
} from '@devinco/ui/grid';
import { Injectable, inject } from '@angular/core';
import { Validators } from '@angular/forms';
import { RestApiService } from '@core/services/rest-api.service';
import ExcelExportUtils from '@core/utils/excel-export.utils';
import { DvPredefinedToolbarMenuItem } from '@devinco/ui/legacy';
import { environment } from '@env/environment';
import { ColumnType } from '@features/vt-grid/grid-options/column-types';
import { ToolbarActionsService } from '@features/vt-grid/services/toolbar-actions.service';
import { Entity } from '@shared/constants/entity';
import { HttpErrorModel } from '@shared/interfaces/http-error.model';
import { VehicleType } from '@shared/constants/vehicle-type';
import { WeekDays } from '@shared/constants/weekdays';
import { TranslateService } from '@ngx-translate/core';
import {
  ColDef,
  EditableCallbackParams,
  GridApi,
  ICellRendererParams,
  IRowNode,
  NewValueParams,
  SortModelItem,
  ValueGetterParams,
} from 'ag-grid-community';
import {
  BehaviorSubject,
  catchError,
  filter,
  Observable,
  of,
  Subject,
  take,
  tap,
} from 'rxjs';
import { TripDto } from '@pages/trips-page/components/trips-grid/interfaces';
import { DialogService } from '@core/services/dialog.service';
import { ExcelExportStatus } from '@shared/constants/excel-export-status';
import { Country } from '@shared/constants/country';
import { UserMetadataService } from '@core/services/user-metadata.service';
import { dvNumberMaskPresets } from '@devinco/ui/dds';
import { DvSplitterEventsService } from '@devinco/ui/dds/splitter';
import { vtPresetActions } from '@features/vt-grid/presets/preset-actions';
import {
  FieldTranslationKey,
  SSGridExcelExportPayload,
} from '@features/vt-grid/interfaces/grid-excel-export';
import { GridContext } from '@features/vt-grid/interfaces/grid-context';

@Injectable({ providedIn: 'root' })
export class TripsGridService {
  private readonly translate = inject(TranslateService);
  private splitterEvents = inject(DvSplitterEventsService);
  private toolbarActionService = inject(ToolbarActionsService);
  private api = inject(RestApiService);
  private dialog = inject(DialogService);
  private userMetadataService = inject(UserMetadataService);

  gridRefreshed$: Subject<void> = new Subject<void>();
  excelLoadingTrigger$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  getColumnDefs(): ColDef<TripDto>[] {
    const fieldsToFilterOut: (keyof TripDto)[] =
      this.userMetadataService.getUserCountry() === Country.Sweden
        ? ['IsPrivateTaxable', 'DistancePrivateTaxable']
        : [];
    return [
      {
        headerName: 'TripsGrid.RegistrationNumber',
        field: 'RegistrationNumber',
        type: [ColumnType.Text],
        filterParams: {
          maxNumConditions: 5,
        },
        suppressFillHandle: true,
      },
      {
        headerName: 'Grid.Username',
        field: 'Username',
        type: [ColumnType.Text],
        pinned: 'left',
        editable: false,
      },
      {
        headerName: 'Grid.Weekday',
        field: 'Weekday',
        type: [ColumnType.TranslatedText],
        valueGetter: (params: ValueGetterParams<TripDto>) => {
          return params.data?.Weekday;
        },
        filterParams: {
          maxNumConditions: 5,
          values: [...Object.keys(WeekDays)],
          valueFormatter: (data: { value: WeekDays | undefined }) => {
            return data.value
              ? this.translate.instant(`General.DaysOfWeek.${data.value}`)
              : '';
          },
        },
        valueFormatter: (data: { value: WeekDays | undefined }) => {
          return data.value
            ? this.translate.instant(`General.DaysOfWeek.${data.value}`)
            : '';
        },
        filter: 'agSetColumnFilter',
        suppressFillHandle: true,
      },
      {
        headerName: 'Grid.StartTime',
        field: 'StartTime',
        type: [ColumnType.DateTime],
        filterParams: {
          maxNumConditions: 5,
        },
        sort: 'desc',
        suppressFillHandle: true,
      },
      {
        headerName: 'Grid.StartLocation',
        field: 'StartLocation',
        type: [ColumnType.Text],
        suppressFillHandle: true,
      },
      {
        headerName: 'Grid.EndTime',
        field: 'EndTime',
        type: [ColumnType.DateTime],
        filterParams: {
          maxNumConditions: 5,
        },
        suppressFillHandle: true,
      },
      {
        headerName: 'Grid.EndLocation',
        field: 'EndLocation',
        type: [ColumnType.Text],
        filterParams: {
          maxNumConditions: 5,
        },
        suppressFillHandle: true,
      },
      {
        headerName: 'Grid.Distance',
        field: 'Distance',
        type: [ColumnType.Km],
        filterParams: {
          maxNumConditions: 5,
        },
        suppressFillHandle: true,
      },
      {
        field: 'SumExpenses',
        type: [ColumnType.NumberDecimal],
        headerName: 'TripsGrid.SumExpenses',
      },
      {
        headerName: 'Grid.Purpose',
        field: 'Purpose',
        type: [ColumnType.Text],
        cellEditor: DvTextCellEditor,
        filterParams: {
          maxNumConditions: 5,
        },
        editable: (params: EditableCallbackParams<TripDto>) => {
          return (
            !this.isBottomPinnedRow(params.node) &&
            !params.api.getRowGroupColumns().length
          );
        },
        // IsPrivateTaxable editability depends on Purpose (obligatory purpose)
        onCellValueChanged: (event: NewValueParams<TripDto>) => {
          if (!event.node) return;
          event.api.refreshCells({
            rowNodes: [event.node],
            columns: ['IsPrivateTaxable'],
            force: true,
          });
        },
      },
      {
        headerName: 'Grid.IsPrivateTaxable',
        field: 'IsPrivateTaxable',
        filterParams: {
          values: [true, false],
          valueFormatter: (data: { value: boolean }) => {
            const key =
              data.value === true
                ? 'Trip.IsPrivateTaxable'
                : 'Trip.IsNotPrivateTaxable';
            return this.translate.instant(key);
          },
        },
        cellEditor: DvCheckboxCellEditor,
        cellEditorParams: { readonly: false },
        editable: (params: EditableCallbackParams<TripDto>) =>
          !this.isBottomPinnedRow(params.node) &&
          this.isPrivateTaxableCheckboxEditable(params.api, params.data),
        onCellValueChanged: (event: NewValueParams) => {
          if (event.context.shouldStopValueChanged) return;

          if (!event.context.isInlineEditingInProgress$.getValue()) {
            if (event.newValue) {
              event.node?.setDataValue(
                'DistancePrivateTaxable',
                event.data.Distance
              );
              this.updateEntity(
                event,
                {
                  DistancePrivateTaxable: event.data.Distance,
                  IsPrivateTaxable: event.newValue,
                },
                true
              ).subscribe();
            } else {
              event.node?.setDataValue('DistancePrivateTaxable', null);
              this.updateEntity(
                event,
                {
                  DistancePrivateTaxable: null,
                  IsPrivateTaxable: event.newValue,
                },
                true
              ).subscribe();
            }
          }
        },
        cellRenderer: DvCheckboxCellRenderer,
        cellRendererParams: (params: ICellRendererParams) => ({
          readonly:
            this.isBottomPinnedRow(params.node) ||
            !this.isPrivateTaxableCheckboxEditable(params.api, params.data),
        }),
        filter: 'agSetColumnFilter',
        suppressFillHandle: true,
      },
      {
        headerName: 'Grid.TaxableDistance',
        field: 'DistancePrivateTaxable',
        editable: (params: EditableCallbackParams<TripDto>) =>
          !this.isBottomPinnedRow(params.node) &&
          !!params.data?.UserID &&
          params.data.VehicleType !== VehicleType.Personal &&
          params.data.VehicleType !== VehicleType.Company &&
          !params.api.getRowGroupColumns().length,
        type: [ColumnType.Km],
        filterParams: {
          maxNumConditions: 5,
        },
        cellEditor: DvNumberCellEditor,
        cellEditorParams: {
          validators: [Validators.required, Validators.min(0.1)],
          fieldMask: dvNumberMaskPresets.FormattedDecimalNumber,
        },
        suppressFillHandle: true,
      },
      {
        headerName: 'Grid.HasExpense',
        field: 'HasExpense',
        type: [ColumnType.Icon],
        cellRendererParams: {
          icon: 'cash',
        } as DvIconCellRendererParams,
        headerComponentParams: {
          icon: 'cash',
          tooltip: this.translate.instant('Grid.Header.Expenses'),
        } as DvIconHeaderRendererParams,
        enableRowGroup: true,
        hide: true,
        filterParams: {
          values: [true, false],
          valueFormatter: (data: { value: boolean }) => {
            const key =
              data.value === true ? 'Trip.HasExpense' : 'Trip.NoExpense';
            return this.translate.instant(key);
          },
        },
        filter: 'agSetColumnFilter',
        pinned: 'left',
        suppressFillHandle: true,
      },
      {
        headerName: 'Grid.MergedTrips',
        field: 'MergedTrip',
        hide: true,
        type: [ColumnType.Icon],
        cellRendererParams: {
          icon: 'source_merge',
        } as DvIconCellRendererParams,
        headerComponentParams: {
          icon: 'source_merge',
          tooltip: this.translate.instant('Grid.Header.MergedTrips'),
        } as DvIconHeaderRendererParams,
        enableRowGroup: true,
        filterParams: {
          values: [true, false],
          valueFormatter: (data: { value: boolean }) => {
            const key = data.value === true ? 'Trip.Merged' : 'Trip.NotMerged';
            return this.translate.instant(key);
          },
        },
        filter: 'agSetColumnFilter',
        pinned: 'left',
        suppressFillHandle: true,
      },
      {
        headerName: 'Grid.IsPrivateOccasional',
        field: 'IsPrivateOccasional',
        type: [ColumnType.Icon],
        hide: true,
        cellRendererParams: {
          icon: 'home',
        } as DvIconCellRendererParams,
        headerComponentParams: {
          icon: 'home',
          tooltip: this.translate.instant('Grid.Header.PrivateOccasional'),
        } as DvIconHeaderRendererParams,
        filterParams: {
          values: [true, false],
          valueFormatter: (data: { value: boolean }) => {
            const key =
              data.value === true
                ? 'Trip.PrivateOccasional'
                : 'Trip.NotPrivateOccasional';
            return this.translate.instant(key);
          },
        },
        filter: 'agSetColumnFilter',
        pinned: 'left',
        suppressFillHandle: true,
      },
      {
        headerName: 'Grid.TripID',
        field: 'TripID',
        type: [ColumnType.Id],
        hide: true,
        filterParams: {
          maxNumConditions: 5,
        },
        suppressFillHandle: true,
      },
      {
        headerName: 'TripsGrid.TrackerID',
        field: 'TrackerID',
        type: [ColumnType.Number],
        hide: true,
        filterParams: {
          maxNumConditions: 5,
        },
        suppressFillHandle: true,
      },
      {
        headerName: 'Grid.Vehicle',
        field: 'TrackerObjectDescription',
        type: [ColumnType.Text],
        hide: true,
        filterParams: {
          maxNumConditions: 5,
        },
        pinned: 'left',
        suppressFillHandle: true,
      },
      {
        headerName: 'Grid.UserID',
        field: 'UserID',
        type: [ColumnType.Number],
        hide: true,
        filterParams: {
          maxNumConditions: 100,
        },
        suppressFillHandle: true,
      },
      {
        headerName: 'Grid.DepartmentID',
        field: 'DepartmentID',
        type: [ColumnType.Number],
        hide: true,
        filterParams: {
          maxNumConditions: 100,
        },
        suppressFillHandle: true,
      },
      {
        headerName: 'TripsGrid.TripDuration',
        field: 'TripDuration',
        type: [ColumnType.Number],
        hide: true,
        suppressFillHandle: true,
      },
      {
        headerName: 'TripsGrid.TripDurationHM',
        field: 'TripDurationHM',
        type: [ColumnType.TimeText],
        hide: true,
        suppressFillHandle: true,
      },
      {
        headerName: 'TripsGrid.StopDuration',
        field: 'StopDuration',
        type: [ColumnType.Number],
        hide: true,
        suppressFillHandle: true,
      },
      {
        headerName: 'TripsGrid.StopDurationHM',
        field: 'StopDurationHM',
        type: [ColumnType.TimeText],
        hide: true,
        suppressFillHandle: true,
      },
      {
        headerName: 'TripsGrid.DepartmentDescription',
        field: 'DepartmentDescription',
        type: [ColumnType.Text],
        hide: true,
        filterParams: {
          maxNumConditions: 100,
        },
        suppressFillHandle: true,
      },
      {
        headerName: 'TripsGrid.Serial',
        field: 'Serial',
        type: [ColumnType.Text],
        hide: true,
        filterParams: {
          maxNumConditions: 5,
        },
        suppressFillHandle: true,
      },
      {
        headerName: 'TripsGrid.VehicleType',
        field: 'VehicleType',
        type: [ColumnType.TranslatedText],
        hide: true,
        filterParams: {
          maxNumConditions: 5,
          values: [...Object.values(VehicleType)],
          valueFormatter: (data: { value: VehicleType }) => {
            return this.translate.instant(`VehicleType.${data.value}`);
          },
        },
        filter: 'agSetColumnFilter',
        suppressFillHandle: true,
      },
      {
        type: [ColumnType.TripType],
        field: 'TripTypeID',
        suppressFillHandle: true,
        hide: true,
      },
      {
        field: 'PrivateTaxableNotAllowed',
        type: [ColumnType.Invisible],
      },
      { field: 'UserEmail', type: [ColumnType.Invisible] },
      {
        headerName: 'TripsGrid.RequirePurposeOnTripTypeChange',
        field: 'RequirePurposeOnTripTypeChange',
        type: [ColumnType.Checkbox],
        hide: true,
        suppressFillHandle: true,
        filterParams: {
          values: [true, false],
          valueFormatter: (data: { value: boolean }) => {
            const key = data.value ? 'General.True' : 'General.False';
            return this.translate.instant(key);
          },
        },
        cellRenderer: DvCheckboxCellRenderer,
        cellRendererParams: { readonly: true },
        filter: 'agSetColumnFilter',
      },
      {
        headerName: 'TripsGrid.IsBusiness',
        field: 'IsBusiness',
        type: [ColumnType.Checkbox],
        hide: true,
        suppressFillHandle: true,
        filterParams: {
          values: [true, false],
          valueFormatter: (data: { value: boolean }) => {
            const key =
              data.value === true ? 'Trip.IsBusiness' : 'Trip.IsNotBusiness';
            return this.translate.instant(key);
          },
        },
        cellEditor: DvCheckboxCellEditor,
        cellRendererParams: (params: ICellRendererParams) => ({
          readonly: !this.isIsBusinessCheckboxEditable(
            params.data?.VehicleType,
            params.node,
            params.api
          ),
        }),
        editable: (params: EditableCallbackParams<TripDto>) =>
          this.isIsBusinessCheckboxEditable(
            params.data?.VehicleType,
            params.node,
            params.api
          ),
        onCellValueChanged: (event: NewValueParams<TripDto>) => {
          if (event.context.shouldStopValueChanged) return;

          this.updateEntity(
            event,
            {
              IsBusiness: event.newValue,
            },
            true
          )
            .pipe(
              tap(() => {
                if (
                  event.newValue === false &&
                  (event.data.VehicleType === VehicleType.Personal ||
                    event.data.VehicleType === VehicleType.Company)
                ) {
                  event.api.getDisplayedRowAtIndex(0)?.setSelected(true, true);
                  event.api.applyServerSideTransaction({
                    remove: [event.data],
                  });
                }
              }),
              take(1)
            )
            .subscribe();
        },
        cellRenderer: DvCheckboxCellRenderer,
        filter: 'agSetColumnFilter',
      },
      {
        field: 'UserTags',
        type: [ColumnType.Text],
        hide: true,
        headerName: 'TripsGrid.UserTags',
      },
      {
        field: 'VehicleTags',
        type: [ColumnType.Text],
        hide: true,
        headerName: 'TripsGrid.VehicleTags',
      },
      {
        field: 'CO2Emission',
        type: [ColumnType.Number],
        headerName: 'TripsGrid.CO2Emission',
        hide: true,
      },
      {
        field: 'KWh',
        type: [ColumnType.NumberDecimal],
        headerName: 'TripsGrid.KWh',
        hide: true,
      },
      {
        field: 'ExternalReference',
        type: [ColumnType.Text],
        headerName: 'TripsGrid.ExternalReference',
        hide: true,
      },
    ].filter(
      column => !fieldsToFilterOut.includes(column.field as keyof TripDto)
    ) as ColDef<TripDto>[];
  }
  getGridActions(
    getTripFileNameFn: () => string,
    hasShowOnlyMinePermission = false
  ): DvToolbarAction[] {
    const rootUrl = `${environment.endpoints.restApi}/report/download`;
    return [
      {
        ...vtPresetActions.excel,
        type: 'raised',
        buttonTextKey: 'TripsGrid.Report',
        menuItems: [
          {
            labelKey: 'TripsGrid.TripLog',
            clickFn: (event: DvToolbarActionEvent) => {
              this.exportTripsExcel(
                `${rootUrl}/TripLog`,
                this.getTripFiltersForExport(event),
                `${ExcelExportUtils.getCapitalizedFileName(
                  this.translate.instant('TripsGrid.TripLog')
                )}${getTripFileNameFn()}`
              );
            },
          },
          {
            labelKey: 'TripsGrid.PrivateTaxableTripsPrUser',
            clickFn: (event: DvToolbarActionEvent) => {
              this.exportTripsExcel(
                `${rootUrl}/PrivateTaxableTripsPrUser`,
                this.getTripFiltersForExport(event),
                `${ExcelExportUtils.getCapitalizedFileName(
                  this.translate.instant('TripsGrid.PrivateTaxableTripsPrUser')
                )}${getTripFileNameFn()}`
              );
            },
          },
          hasShowOnlyMinePermission
            ? {
                labelKey: 'TripsGrid.BusinessTripsForPersonalVehicle',
                clickFn: (event: DvToolbarActionEvent) => {
                  this.exportTripsExcel(
                    `${rootUrl}/BusinessTripsForPersonalVehicle`,
                    this.getTripFiltersForExport(event),
                    `${ExcelExportUtils.getCapitalizedFileName(
                      this.translate.instant(
                        'TripsGrid.BusinessTripsForPersonalVehicle'
                      )
                    )}${getTripFileNameFn()}`
                  );
                },
              }
            : undefined,
          {
            labelKey: 'TripsGrid.TripsPrPurpose',
            clickFn: (event: DvToolbarActionEvent) => {
              this.exportTripsExcel(
                `${rootUrl}/TripsPrPurpose`,
                this.getTripFiltersForExport(event),
                `${ExcelExportUtils.getCapitalizedFileName(
                  this.translate.instant('TripsGrid.TripsPrPurpose')
                )}${getTripFileNameFn()}`
              );
            },
          },
          {
            labelKey: 'TripsGrid.PrivateOccasionalPrUser',
            clickFn: (event: DvToolbarActionEvent) => {
              this.exportTripsExcel(
                `${rootUrl}/PrivateOccasionalTripsPrUser`,
                this.getTripFiltersForExport(event),
                `${ExcelExportUtils.getCapitalizedFileName(
                  this.translate.instant('TripsGrid.PrivateOccasionalPrUser')
                )}${getTripFileNameFn()}`
              );
            },
          },
          {
            labelKey: 'TripsGrid.Export',
            clickFn: (event: DvToolbarActionEvent) => {
              const translationKeys = this.getGridColumnFieldTranslationKeys();
              const payload: SSGridExcelExportPayload = {
                ...this.getTripFiltersForExport(event),
                fieldsAndTranslationKeys: translationKeys,
                /** required in order to get data */
                fields: translationKeys.map(item => item.field),
              };
              this.exportTripsExcel(
                `${rootUrl}/TripLogGrid`,
                payload,
                `${ExcelExportUtils.getCapitalizedFileName(
                  this.translate.instant('TripsGrid.ExportTrip')
                )}${getTripFileNameFn()}`
              );
            },
          },
          {
            labelKey: 'TripsGrid.VismaPayroll',
            clickFn: (event: DvToolbarActionEvent) => {
              this.exportTripsExcel(
                `${rootUrl}/VismaPayroll`,
                this.getTripFiltersForExport(event),
                `${ExcelExportUtils.getCapitalizedFileName(
                  this.translate.instant('TripsGrid.VismaPayroll')
                )}${getTripFileNameFn()}`
              );
            },
          },
        ].filter(menuItem => menuItem) as DvPredefinedToolbarMenuItem[],
      },
      {},
      vtPresetActions.reset,
      vtPresetActions.fit,
      {
        icon: 'refresh',
        tooltip: 'Grid.ReloadData',
        tooltipKey: 'Grid.ReloadData',
        clickFn: params => {
          const { api } = params;
          api.refreshServerSide({ route: [], purge: true });
          this.gridRefreshed$.next();
        },
      },
      {},
      {
        icon: 'edit',
        clickFn: () => this.splitterEvents.isSplitterOpened$.next(true),
        disabled: true,
        tooltip: this.translate.instant('Grid.Edit'),
      },
    ];
  }

  private getGridColumnFieldTranslationKeys(): FieldTranslationKey[] {
    return this.getColumnDefs()
      .filter(colDef => {
        const isInvisible = Array.isArray(colDef.type)
          ? colDef.type.includes(ColumnType.Invisible)
          : colDef.type === ColumnType.Invisible;

        return (
          colDef.field !== 'TripTypeID' &&
          colDef.field !== 'TripID' &&
          !isInvisible
        );
      })
      .map(colDef => ({
        field: colDef.field!,
        translationKey: colDef.headerName ?? colDef.field!,
      }));
  }

  private getTripFiltersForExport = (
    event: DvToolbarActionEvent<TripDto>
  ): SSGridExcelExportPayload => {
    const context = event.context as GridContext;
    const api = event.api;
    const filterModel = {
      ...event.api.getFilterModel(),
      ...context.extraFilter,
    };

    return {
      entity: Entity.Trip,
      fields: [],
      filterModel,
      customFilter: context.customFilter,
      sortModel: api
        .getColumnState()
        .filter(col => col.sort !== null)
        .map(function (c) {
          return { sort: c.sort, colId: c.colId };
        }) as SortModelItem[],
      quickFilterText: context.quickFilterText,
    } satisfies SSGridExcelExportPayload;
  };

  private exportTripsExcel(
    url: string,
    payload: SSGridExcelExportPayload,
    fileName?: string
  ): void {
    this.excelLoadingTrigger$.next(true);
    this.toolbarActionService
      .downloadExcel(url, payload, Entity.Trip, fileName)
      .pipe(
        filter(value => !!value),
        take(1)
      )
      .subscribe(value => {
        if (value === ExcelExportStatus.Empty) {
          this.dialog.open({
            type: 'error',
            title: 'TripsGrid.EmptyReportTitle',
            contentText: 'TripsGrid.NoDataInExcel',
            confirmText: 'General.Ok',
          });
        }
        this.excelLoadingTrigger$.next(false);
      });
  }

  private updateEntity(
    event: NewValueParams<TripDto>,
    payload: object,
    refreshSidesheet = false
  ): Observable<number | undefined> {
    const data = event.data;
    return this.api.upsert(Entity.Trip, data.TripID, payload).pipe(
      catchError((resp: { error: HttpErrorModel }) => {
        event.context.shouldStopValueChanged = true;
        const initRowData = resp.error.InitialRowData;
        if (initRowData) {
          Object.keys(initRowData).forEach(key => {
            event.node?.setDataValue(key, initRowData[key]);
          });
        }
        setTimeout(() => {
          event.context.shouldStopValueChanged = false;
        });
        return of();
      }),
      tap(() => {
        event.api.flashCells({
          rowNodes: [event.node!],
          columns: [event.column],
        });

        if (refreshSidesheet) {
          this.gridRefreshed$.next();
        }
      }),
      take(1)
    );
  }

  private isPrivateTaxableCheckboxEditable(
    api?: GridApi,
    data?: TripDto
  ): boolean {
    if (!data) return false;

    if (
      data.VehicleType === VehicleType.Corporate &&
      this.userMetadataService.getUserCountry() === Country.Sweden
    )
      return false;

    if (
      data.VehicleType === VehicleType.Corporate &&
      data.IsPrivateTaxable &&
      !data.Purpose?.trim() &&
      data.RequirePurposeOnTripTypeChange
    )
      return false;

    return (
      !!data.UserID &&
      data.VehicleType !== VehicleType.Personal &&
      data.VehicleType !== VehicleType.Company &&
      !api?.getRowGroupColumns().length &&
      !data.PrivateTaxableNotAllowed
    );
  }

  private isIsBusinessCheckboxEditable(
    vehicleType?: VehicleType,
    node?: IRowNode<TripDto>,
    api?: GridApi<TripDto>
  ): boolean {
    return (
      !this.isBottomPinnedRow(node) &&
      !api?.getRowGroupColumns().length &&
      vehicleType !== VehicleType.Corporate
    );
  }

  private isBottomPinnedRow(node?: IRowNode): boolean {
    return !!node?.rowPinned;
  }
}
